import * as Yup from 'yup';
import { SUPPORTED_SPORT_CODES } from '@low6-pickem/sports-config';

/**
 * Get earliest game time from questions
 * Uses sportcc_match_data.matchDate if available, falls back to start_utc
 */
function getEarliestGameTime(questions: any[]): Date | null {
  const gameTimes = questions
    .map(q => q.sportcc_match_data?.matchDate || q.start_utc)
    .filter((time): time is string => !!time)
    .map(time => new Date(time))
    .filter(d => !isNaN(d.getTime()));

  if (gameTimes.length === 0) return null;
  return new Date(Math.min(...gameTimes.map(d => d.getTime())));
}

/**
 * Create dynamic validation schema based on questions
 *
 * @param questions - Array of questions for this pickem (used to validate dates against game times)
 * @returns Yup validation schema
 */
export const createPickemValidationSchema = (questions: any[] = []) => {
  const earliestGame = getEarliestGameTime(questions);

  return Yup.object({
    tenant_id: Yup.string().required('Tenant is required'),
    title_en: Yup.string()
      .required('English title is required')
      .min(3, 'English title must be at least 3 characters')
      .max(200, 'English title must be less than 200 characters'),
    title_fr: Yup.string()
      .min(3, 'Spanish title must be at least 3 characters')
      .max(200, 'Spanish title must be less than 200 characters'),
    description_en: Yup.string().max(1000, 'Description must be less than 1000 characters'),
    description_fr: Yup.string().max(1000, 'Description must be less than 1000 characters'),
    start: Yup.string()
      .optional()
      .test(
        'start-required-if-open',
        'Start date is required when open date is set',
        function (value) {
          const { allow_selection_start } = this.parent;
          if (allow_selection_start && !value) return false;
          return true;
        }
      )
      .test(
        'start-before-first-game',
        earliestGame
          ? `Start date must be before the first game (${earliestGame.toLocaleString()})`
          : 'Start date is invalid',
        function (value) {
          if (!value || !earliestGame) return true;
          return new Date(value) < earliestGame;
        }
      ),
    allow_selection_start: Yup.string()
      .optional()
      .test('open-before-start', 'Open date must be before start date', function (value) {
        const { start } = this.parent;
        if (!value || !start) return true;
        return new Date(value) < new Date(start);
      })
      .test(
        'open-before-first-game',
        earliestGame
          ? `Open date must be before the first game (${earliestGame.toLocaleString()})`
          : 'Open date is invalid',
        function (value) {
          if (!value || !earliestGame) return true;
          return new Date(value) < earliestGame;
        }
      ),
    pickem_status: Yup.string(),
    sport: Yup.string()
      .required('Sport is required')
      .oneOf(
        [...SUPPORTED_SPORT_CODES, 'NHL', 'Mixed'],
        `Sport must be ${SUPPORTED_SPORT_CODES.join(', ')}, NHL, or Mixed`
      ),
    prize_text_en: Yup.string().max(500, 'Prize text must be less than 500 characters'),
    prize_text_fr: Yup.string().max(500, 'Prize text must be less than 500 characters'),
    prize_image_en: Yup.string().url('Must be a valid URL').nullable(),
    prize_image_fr: Yup.string().url('Must be a valid URL').nullable(),
    main_image_en: Yup.string().url('Must be a valid URL').nullable(),
    main_image_fr: Yup.string().url('Must be a valid URL').nullable(),
    prizesBreakdown: Yup.string(),
  });
};

/**
 * Legacy static schema - deprecated, use createPickemValidationSchema instead
 * @deprecated Use createPickemValidationSchema for proper validation
 */
export const pickemValidationSchema = createPickemValidationSchema();
